import { fromMinorUnits } from '@/api/map/agent'
import type { PnlSummary } from '@/data/api'
import { unstopped, type Account, type Role } from '@/data/types'
import { STOP_COPY } from '@/lib/status'

/** analytics-service Money — minor units as string. */
export type MoneyMinor = string

export interface AnalyticsUnavailable {
  code: string
  reason: string
  trackedBy?: string
}

export interface AnalyticsPnlSummary {
  asOf?: string
  martBuiltAt?: string | null
  martStale?: boolean
  agentId: string
  book: {
    turnoverMinor: MoneyMinor
    grossNetMinor: MoneyMinor
    payoutMinor?: MoneyMinor
    betsSettled: number
    marginPct?: number | null
  }
  earnings:
    | { netMinor: MoneyMinor; source: 'settlement_postings' }
    | AnalyticsUnavailable
}

export interface AnalyticsDailyRow {
  date: string
  betsPlaced?: number
  turnoverMinor: MoneyMinor
  betsSettled?: number
  bookNetMinor: MoneyMinor
  activeMembers?: number
  bookMargin?: number | null
}

export interface AnalyticsDailyResponse {
  asOf?: string
  rows: AnalyticsDailyRow[]
}

export interface AnalyticsMemberPnlRow {
  memberId: string
  name: string
  parentId?: string
  betsPlaced?: number
  betsSettled?: number
  turnoverMinor: MoneyMinor
  memberNetMinor?: MoneyMinor
  bookNetMinor: MoneyMinor
}

export interface AnalyticsMemberPnlPage {
  asOf?: string
  martBuiltAt?: string | null
  martStale?: boolean
  page?: number
  size?: number
  total?: number
  rows: AnalyticsMemberPnlRow[]
}

/** Console view of one member-pnl row (major ₹). */
export type MemberPnlRowView = {
  account: Account
  turnover: number
  memberPnl: number
  commission: number
  netPnl: number
  bets: number
}

/**
 * Full member-pnl result for the console.
 * Live analytics has no per-member commission on this endpoint — commission stays 0
 * until ledger-backed `/commission` (slice a6). Fixtures may still populate commission.
 */
export type MemberPnlResult = {
  rows: MemberPnlRowView[]
  asOf?: string
  martStale?: boolean
  /** False when rows came from analytics OpenAPI (no commission field). */
  commissionAvailable: boolean
}

export function isUnavailable(value: unknown): value is AnalyticsUnavailable {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'code' in value &&
      typeof (value as AnalyticsUnavailable).code === 'string' &&
      'reason' in value,
  )
}

/**
 * Map analytics `/pnl` + `/daily` → console `PnlSummary`.
 * Event-level `rows` are not in the analytics contract — left empty (no invented events).
 * Earnings never derived from share_bp: Unavailable stays unavailable.
 */
export function toPnlSummary(
  pnl: AnalyticsPnlSummary,
  daily: AnalyticsDailyRow[] | undefined,
  opts: { page?: number; pageSize?: number; myRole?: Role } = {},
): PnlSummary {
  const page = opts.page ?? 1
  const pageSize = opts.pageSize ?? 50
  const turnover = fromMinorUnits(pnl.book.turnoverMinor)
  const netPnl = fromMinorUnitsSigned(pnl.book.grossNetMinor)
  const bets = pnl.book.betsSettled ?? 0
  const margin =
    pnl.book.marginPct === null || pnl.book.marginPct === undefined
      ? turnover === 0
        ? 0
        : netPnl / turnover
      : pnl.book.marginPct

  let earningsMajor: number | null = null
  let earningsUnavailable: AnalyticsUnavailable | undefined
  if (isUnavailable(pnl.earnings)) {
    earningsUnavailable = pnl.earnings
  } else {
    earningsMajor = fromMinorUnitsSigned(pnl.earnings.netMinor)
  }

  const splitByRole: { role: Role; amount: number }[] = []
  if (earningsMajor !== null && opts.myRole) {
    splitByRole.push({ role: opts.myRole, amount: earningsMajor })
  }

  const dailyMapped =
    daily?.map((d) => ({
      date: d.date,
      netPnl: fromMinorUnitsSigned(d.bookNetMinor),
      turnover: fromMinorUnits(d.turnoverMinor),
      commission: 0,
      bets: d.betsSettled ?? d.betsPlaced ?? 0,
    })) ?? []

  return {
    rows: [],
    total: 0,
    page,
    pageSize,
    totals: {
      turnover,
      memberPnl: -netPnl,
      commission: 0,
      netPnl,
      bets,
      members: 0,
      margin,
    },
    daily: dailyMapped,
    splitByRole,
    asOf: pnl.asOf,
    martStale: pnl.martStale,
    earningsUnavailable,
  }
}

export function toMemberPnlRows(page: AnalyticsMemberPnlPage): MemberPnlRowView[] {
  return (page.rows ?? []).map((row) => {
    const memberPnl =
      row.memberNetMinor !== undefined
        ? fromMinorUnitsSigned(row.memberNetMinor)
        : -fromMinorUnitsSigned(row.bookNetMinor)
    const bookNet = fromMinorUnitsSigned(row.bookNetMinor)
    return {
      account: stubMemberAccount(row.memberId, row.name, row.parentId),
      turnover: fromMinorUnits(row.turnoverMinor),
      memberPnl,
      // OpenAPI MemberPnlRow has no commission — never invent from share_bp.
      commission: 0,
      netPnl: bookNet,
      bets: row.betsSettled ?? row.betsPlaced ?? 0,
    }
  })
}

/** Merge one or more analytics pages into a console MemberPnlResult. */
export function toMemberPnlResult(pages: AnalyticsMemberPnlPage[]): MemberPnlResult {
  const first = pages[0]
  const rows = pages.flatMap((p) => toMemberPnlRows(p))
  return {
    rows,
    asOf: first?.asOf,
    martStale: first?.martStale,
    commissionAvailable: false,
  }
}

/** Signed minor string → major units (allows negative book/member nets). */
export function fromMinorUnitsSigned(minor: string | undefined): number {
  if (!minor) return 0
  const n = Number(minor)
  if (!Number.isFinite(n)) return 0
  return n / 100
}

function stubMemberAccount(id: string, name: string, parentId?: string): Account {
  return {
    id,
    username: name,
    displayName: name,
    role: 'player',
    parentId: parentId ?? null,
    path: parentId ? [parentId] : [],
    status: 'active',
    createdAt: '',
    lastActiveAt: null,
    creditLimit: 0,
    balance: 0,
    distributedBalance: 0,
    exposure: 0,
    settlement: 0,
    share: { sportsbook: 0, casino: 0 },
    commission: { fancy: 0, bookmaker: 0, matchOdds: 0 },
    betLimits: { min: 0, max: 0 },
    games: {
      casino: true,
      cardGame: true,
      liveGame: true,
      sportsbook: true,
    },
    childCount: 0,
    memberCount: 0,
    flags: [],
    // A reporting stub, not a hierarchy row: nothing here can be stopped.
    ...unstopped('active', STOP_COPY.notWiredLive),
  }
}
